import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '../../lib/i18n';
import { api } from '../../lib/api';
import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  InlineActions,
  Input,
  Modal,
  PageHeader,
  RefreshButton,
  SearchInput,
  SectionHeader,
  Select,
  StatCard,
  StatsGrid,
  StatusDot,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '../../components/ui';
import {
  CheckCircle,
  Clock,
  Copy,
  Download,
  Key,
  Network,
  Plus,
  Server,
  Terminal,
  Users,
  Wifi,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Types ──────────────────────────────────────────────────────────────────────

interface Health {
  tenants: number;
  nodes: number;
  online: number;
}

interface Tenant {
  id: string;
  slug: string;
  name: string;
  enabled: boolean;
}

interface ProvisionToken {
  id: string;
  tenant_slug: string;
  token: string;
  expires_at: string | null;
  max_uses: number;
  use_count: number;
  /** Hotový enroll příkaz — vrací backend (mesh.tokens), UI adresu nikdy neskládá samo. */
  install_cmd?: string;
  /** Veřejná ACM API URL, kterou vrací backend, pro dostavbu příkazu v UI (preview token). */
  public_url?: string;
}

interface Node {
  node_id: string;
  tenant_slug: string;
  hostname: string;
  status: string;
  arch: string;
  agent_version?: string;
  acm_version?: string;
  ip_private?: string;
  modules?: string[];
  last_seen: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function copyText(text: string, label = i18n.t('provision.copied')) {
  navigator.clipboard.writeText(text);
  toast.success(label);
}

// ── Tenanty ────────────────────────────────────────────────────────────────────

function TenantsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '' });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['provision-tenants'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; tenants: Tenant[] }>('mesh.tenants', {
        action: 'list',
      });
      return { ok: r.ok, items: r.tenants ?? [] };
    },
  });

  const createMut = useMutation({
    mutationFn: (name: string) => {
      const slug = name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'tenant';
      return api.mcp('mesh.tenants', { action: 'create', slug, name });
    },
    onSuccess: () => {
      toast.success(t('provision.tenantCreated'));
      setShowModal(false);
      setForm({ name: '' });
      qc.invalidateQueries({ queryKey: ['provision-tenants'] });
      qc.invalidateQueries({ queryKey: ['provision-health'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const toggleMut = useMutation({
    mutationFn: ({ slug, enabled }: { slug: string; enabled: boolean }) =>
      api.mcp('mesh.tenants', { action: 'update', slug, enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['provision-tenants'] }),
    onError: (e) => toast.error((e as Error).message),
  });

  const deleteMut = useMutation({
    mutationFn: (slug: string) => api.mcp('mesh.tenants', { action: 'delete', slug }),
    onSuccess: () => {
      toast.success(t('provision.tenantDeleted'));
      qc.invalidateQueries({ queryKey: ['provision-tenants'] });
      qc.invalidateQueries({ queryKey: ['provision-health'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const tenants = data?.items ?? [];

  return (
    <div className='space-y-4'>
      <SectionHeader
        title={t('provision.tenantsTitle', { count: tenants.length })}
        actions={
          <div className='flex items-center gap-2'>
            <RefreshButton onRefresh={refetch} loading={isLoading} />
            <Button
              variant='primary'
              size='sm'
              icon={<Plus size={13} />}
              onClick={() => setShowModal(true)}
            >
              {t('provision.newTenantButton')}
            </Button>
          </div>
        }
      />

      {isLoading
        ? (
          <div className='space-y-2'>
            {[...Array(3)].map((_, i) => (
              <div key={i} className='h-16 rounded-2xl bg-gray-800/30 animate-pulse' />
            ))}
          </div>
        )
        : tenants.length === 0
        ? (
          <EmptyState
            icon={<Users size={28} />}
            title={t('provision.noTenantsTitle')}
            description={t('provision.noTenantsDescription')}
            action={
              <Button
                variant='primary'
                size='sm'
                icon={<Plus size={13} />}
                onClick={() => setShowModal(true)}
              >
                {t('provision.newTenantButton')}
              </Button>
            }
          />
        )
        : (
          <div className='space-y-2'>
            {tenants.map((tenant) => (
              <Card key={tenant.id} className='group'>
                <CardContent className='p-4 flex items-center justify-between'>
                  <div className='flex items-center gap-3 min-w-0'>
                    <StatusDot status={tenant.enabled ? 'active' : 'stopped'} />
                    <div className='min-w-0'>
                      <span className='text-sm font-semibold text-white'>{tenant.name}</span>
                      <span className='ml-2 font-mono text-xs text-gray-500'>{tenant.slug}</span>
                    </div>
                    <Badge variant={tenant.enabled ? 'green' : 'gray'}>
                      {tenant.enabled ? t('provision.activeBadge') : t('provision.inactiveBadge')}
                    </Badge>
                  </div>
                  <InlineActions.Row
                    onDelete={() => deleteMut.mutate(tenant.slug)}
                    confirmDelete={t('provision.confirmDeleteTenant', { name: tenant.name })}
                  >
                    <InlineActions.IconBtn
                      icon={<CheckCircle size={13} />}
                      title={tenant.enabled
                        ? t('provision.deactivateAction')
                        : t('provision.activateAction')}
                      onClick={() =>
                        toggleMut.mutate({ slug: tenant.slug, enabled: !tenant.enabled })}
                      variant={tenant.enabled ? 'default' : 'primary'}
                    />
                  </InlineActions.Row>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={t('provision.newTenantButton')}
      >
        <div className='space-y-4'>
          <div>
            <label className='block text-xs text-gray-400 mb-1.5'>
              {t('provision.tenantNameLabel')}
            </label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ name: e.target.value })}
              placeholder={t('provision.tenantNamePlaceholder')}
              onKeyDown={(e) => e.key === 'Enter' && form.name && createMut.mutate(form.name)}
              autoFocus
            />
            <p className='text-xs text-gray-600 mt-1'>{t('provision.slugAutoDerivedHint')}</p>
          </div>
          <div className='flex justify-end gap-2 pt-1'>
            <Button variant='ghost' onClick={() => setShowModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant='primary'
              onClick={() => createMut.mutate(form.name)}
              loading={createMut.isPending}
              disabled={!form.name.trim()}
            >
              {t('provision.createButton')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Tokeny ─────────────────────────────────────────────────────────────────────

function TokensTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [showInstall, setShowInstall] = useState<ProvisionToken | null>(null);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [maxUses, setMaxUses] = useState('1');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['provision-tokens'],
    queryFn: async () => {
      const r = await api.mcp<{
        ok: boolean;
        tokens: Array<
          ProvisionToken & { token_preview?: string; tenant_slug?: string }
        >;
      }>('mesh.tokens', { action: 'list' });
      return {
        ok: r.ok,
        items: (r.tokens ?? []).map((tok) => ({
          ...tok,
          token: tok.token ?? tok.token_preview ?? '',
        })),
      };
    },
  });

  const { data: tenantsData } = useQuery({
    queryKey: ['provision-tenants'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; tenants: Tenant[] }>('mesh.tenants', {
        action: 'list',
      });
      return { ok: r.ok, items: r.tenants ?? [] };
    },
  });

  const createMut = useMutation({
    mutationFn: (body: { tenant_slug: string; max_uses: number }) =>
      api.mcp('mesh.tokens', { action: 'create', ...body }),
    onSuccess: () => {
      toast.success(t('provision.tokenCreated'));
      setShowModal(false);
      setSelectedTenant('');
      setMaxUses('1');
      qc.invalidateQueries({ queryKey: ['provision-tokens'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) =>
      api.mcp('mesh.tokens', { action: 'revoke', token_id: id }),
    onSuccess: () => {
      toast.success(t('provision.tokenRevoked'));
      qc.invalidateQueries({ queryKey: ['provision-tokens'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const tokens = data?.items ?? [];
  const tenants = tenantsData?.items ?? [];

  // Enroll device binary install.sh (skip streaming/binary). Adresu vrací
  // backend (mesh.tokens.public_url / install_cmd) — UI ji neskládá samo.
  const installUrl = (token: ProvisionToken) =>
    token.install_cmd ??
    (token.public_url
      ? `curl -fsSL '${token.public_url}/apiext/provision/install.sh?token=${token.token}' | bash`
      : t('provision.installUrlUnavailable'));

  const isExpired = (tok: ProvisionToken) =>
    tok.expires_at !== null && new Date(tok.expires_at) < new Date();
  const isExhausted = (tok: ProvisionToken) => tok.max_uses > 0 && tok.use_count >= tok.max_uses;

  return (
    <div className='space-y-4'>
      <SectionHeader
        title={t('provision.tokensTitle', { count: tokens.length })}
        actions={
          <div className='flex items-center gap-2'>
            <RefreshButton onRefresh={refetch} loading={isLoading} />
            <Button
              variant='primary'
              size='sm'
              icon={<Plus size={13} />}
              onClick={() => setShowModal(true)}
            >
              {t('provision.newTokenButton')}
            </Button>
          </div>
        }
      />

      {isLoading
        ? (
          <div className='space-y-2'>
            {[...Array(3)].map((_, i) => (
              <div key={i} className='h-14 rounded-2xl bg-gray-800/30 animate-pulse' />
            ))}
          </div>
        )
        : tokens.length === 0
        ? (
          <EmptyState
            icon={<Key size={28} />}
            title={t('provision.noTokensTitle')}
            description={t('provision.noTokensDescription')}
            action={
              <Button
                variant='primary'
                size='sm'
                icon={<Plus size={13} />}
                onClick={() => setShowModal(true)}
              >
                {t('provision.newTokenButton')}
              </Button>
            }
          />
        )
        : (
          <div className='border border-gray-800 rounded-2xl overflow-hidden'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='bg-gray-900/50 border-b border-gray-800 text-xs text-gray-500 uppercase tracking-wider'>
                  <th className='px-4 py-3 text-left'>Tenant</th>
                  <th className='px-4 py-3 text-left'>Token</th>
                  <th className='px-4 py-3 text-center'>{t('provision.colUses')}</th>
                  <th className='px-4 py-3 text-center'>{t('provision.colValidity')}</th>
                  <th className='px-4 py-3 text-right'>{t('provision.colActions')}</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-800/60'>
                {tokens.map((tk) => {
                  const expired = isExpired(tk);
                  const exhausted = isExhausted(tk);
                  const invalid = expired || exhausted;
                  return (
                    <tr key={tk.id} className='group hover:bg-gray-800/20 transition-colors'>
                      <td className='px-4 py-3'>
                        <span className='text-xs font-mono text-gray-300'>{tk.tenant_slug}</span>
                      </td>
                      <td className='px-4 py-3'>
                        <code className='text-xs text-blue-400 font-mono'>
                          {tk.token.slice(0, 12)}…
                        </code>
                      </td>
                      <td className='px-4 py-3 text-center'>
                        <span
                          className={`text-xs font-mono ${
                            exhausted ? 'text-red-400' : 'text-gray-400'
                          }`}
                        >
                          {tk.use_count}/{tk.max_uses === 0 ? '∞' : tk.max_uses}
                        </span>
                      </td>
                      <td className='px-4 py-3 text-center'>
                        {expired
                          ? <Badge variant='red'>{t('provision.expiredBadge')}</Badge>
                          : tk.expires_at
                          ? (
                            <span className='text-xs text-gray-400 font-mono'>
                              {new Date(tk.expires_at).toLocaleDateString('cs')}
                            </span>
                          )
                          : <Badge variant='gray'>{t('provision.noLimitBadge')}</Badge>}
                      </td>
                      <td className='px-4 py-3'>
                        <InlineActions.Row
                          visible
                          onDelete={() => revokeMut.mutate(tk.id)}
                          confirmDelete={t('provision.confirmRevokeToken', {
                            tenant: tk.tenant_slug,
                          })}
                        >
                          <InlineActions.IconBtn
                            icon={<Copy size={13} />}
                            title={t('provision.copyTokenAction')}
                            onClick={() => copyText(tk.token, t('provision.tokenCopied'))}
                          />
                          {!invalid && (
                            <InlineActions.IconBtn
                              icon={<Download size={13} />}
                              title={t('provision.showInstallAction')}
                              onClick={() => setShowInstall(tk)}
                              variant='primary'
                            />
                          )}
                        </InlineActions.Row>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      {/* Modal — nový token */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={t('provision.newTokenModalTitle')}
      >
        <div className='space-y-4'>
          <div>
            <label className='block text-xs text-gray-400 mb-1.5'>Tenant</label>
            <Select
              className='w-full'
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
            >
              <option value=''>{t('provision.selectTenantOption')}</option>
              {tenants.map((tenant) => (
                <option key={tenant.slug} value={tenant.slug}>
                  {tenant.name} ({tenant.slug})
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className='block text-xs text-gray-400 mb-1.5'>
              {t('provision.maxUsesLabel')}
            </label>
            <Input
              type='number'
              min='0'
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder={t('provision.maxUsesPlaceholder')}
            />
            <p className='text-xs text-gray-600 mt-1'>{t('provision.maxUsesHint')}</p>
          </div>
          <div className='flex justify-end gap-2 pt-1'>
            <Button variant='ghost' onClick={() => setShowModal(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant='primary'
              onClick={() =>
                createMut.mutate({
                  tenant_slug: selectedTenant,
                  max_uses: parseInt(maxUses) || 0,
                })}
              loading={createMut.isPending}
              disabled={!selectedTenant}
            >
              {t('provision.createButton')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal — install příkaz */}
      <Modal
        open={!!showInstall}
        onClose={() => setShowInstall(null)}
        title={t('provision.installModalTitle')}
      >
        {showInstall && (
          <div className='space-y-4'>
            <p className='text-xs text-gray-400'>
              {t('provision.installCommandIntro')}
              {showInstall.max_uses === 1
                ? t('provision.maxUsesOne')
                : showInstall.max_uses > 1
                ? t('provision.maxUsesN', { count: showInstall.max_uses })
                : ''}.
            </p>
            <div className='relative'>
              <pre className='bg-gray-950 border border-gray-700 rounded-xl p-4 text-xs font-mono text-green-400 break-all whitespace-pre-wrap leading-relaxed'>
                {installUrl(showInstall)}
              </pre>
              <button
                className='absolute top-2 right-2 p-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-colors'
                onClick={() => copyText(installUrl(showInstall), t('provision.commandCopied'))}
                title={t('provision.copyAction')}
              >
                <Copy size={13} />
              </button>
            </div>
            <div className='text-xs text-gray-500 space-y-1'>
              <p>
                • {t('provision.installStep1')}{' '}
                <code className='text-gray-400'>/webhook/join</code>
              </p>
              <p>
                • {t('provision.installStep2')} (<code className='text-gray-400'>
                  /opt/acm-proxy
                </code>)
              </p>
              <p>
                • {t('provision.installStep3')}{' '}
                <code className='text-gray-400'>acm-proxy</code>
              </p>
            </div>
            <div className='flex justify-end'>
              <Button variant='ghost' onClick={() => setShowInstall(null)}>
                {t('provision.closeButton')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Nody ───────────────────────────────────────────────────────────────────────

function NodesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [cmdInput, setCmdInput] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['provision-nodes'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; nodes: Node[] }>('mesh.nodes', {
        action: 'list',
      });
      return { ok: r.ok, items: r.nodes ?? [] };
    },
    refetchInterval: 30_000,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      api.mcp('mesh.nodes', { action: 'remove', node_id: id }),
    onSuccess: () => {
      toast.success(t('provision.nodeRemoved'));
      qc.invalidateQueries({ queryKey: ['provision-nodes'] });
      qc.invalidateQueries({ queryKey: ['provision-health'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  // Shell cmd na node byl odstraněn (mesh_cmd 2026-06-05) — žádný REST ani tool.
  const cmdMut = useMutation({
    mutationFn: async (_args: { id: string; cmd: string }) => {
      throw new Error(
        t(
          'provision.shellCmdRemoved',
          'Vzdálený shell příkaz na node byl odstraněn. Použij fleet/agent tooly.',
        ),
      );
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const allNodes = data?.items ?? [];
  const q = search.toLowerCase();
  const nodes = allNodes.filter((n) =>
    n.hostname.toLowerCase().includes(q) ||
    n.tenant_slug.toLowerCase().includes(q) ||
    n.node_id.toLowerCase().includes(q) ||
    (n.ip_private ?? '').includes(q)
  );

  return (
    <div className='space-y-4'>
      <SectionHeader
        title={t('provision.nodesTitle', { count: allNodes.length })}
        actions={<RefreshButton onRefresh={refetch} loading={isLoading} />}
      />

      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder={t('provision.searchNodesPlaceholder')}
        size='md'
        resultCount={nodes.length}
        totalCount={allNodes.length}
      />

      {isLoading
        ? (
          <div className='space-y-2'>
            {[...Array(4)].map((_, i) => (
              <div key={i} className='h-16 rounded-2xl bg-gray-800/30 animate-pulse' />
            ))}
          </div>
        )
        : nodes.length === 0
        ? (
          <EmptyState
            icon={<Server size={28} />}
            title={search ? t('provision.noResultsTitle') : t('provision.noNodesTitle')}
            description={search
              ? t('provision.noResultsDescription')
              : t('provision.noNodesDescription')}
          />
        )
        : (
          <div className='space-y-2'>
            {nodes.map((n) => (
              <Card key={n.node_id} className='group'>
                <CardContent className='p-4 flex items-center justify-between gap-4'>
                  <div className='flex items-center gap-3 min-w-0 flex-1'>
                    <StatusDot status={n.status} size='md' />
                    <div className='min-w-0'>
                      <div className='flex items-center gap-2 flex-wrap'>
                        <span className='text-sm font-semibold text-white'>{n.hostname}</span>
                        <Badge variant={n.status === 'online' ? 'green' : 'gray'}>
                          {n.status}
                        </Badge>
                        <span className='text-xs text-gray-500 font-mono'>{n.tenant_slug}</span>
                      </div>
                      <div className='flex items-center gap-3 mt-0.5 text-[10px] text-gray-600 font-mono flex-wrap'>
                        <span>{n.node_id.slice(0, 12)}…</span>
                        {n.arch && <span>{n.arch}</span>}
                        {n.ip_private && <span>{n.ip_private}</span>}
                        {n.agent_version && <span>proxy {n.agent_version}</span>}
                        <span className='flex items-center gap-1'>
                          <Clock size={9} />
                          {relativeTime(n.last_seen)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <InlineActions.Row
                    onDelete={() => deleteMut.mutate(n.node_id)}
                    confirmDelete={t('provision.confirmRemoveNode', { hostname: n.hostname })}
                  >
                    <InlineActions.IconBtn
                      icon={<Terminal size={13} />}
                      title={t('provision.sendCommandAction')}
                      onClick={() => {
                        setSelectedNode(n);
                        setCmdInput('');
                      }}
                      variant='primary'
                    />
                  </InlineActions.Row>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      {/* Modal — shell příkaz */}
      <Modal
        open={!!selectedNode}
        onClose={() => setSelectedNode(null)}
        title={t('provision.commandModalTitle', { hostname: selectedNode?.hostname })}
      >
        {selectedNode && (
          <div className='space-y-4'>
            <p className='text-xs text-gray-500'>
              {t('provision.commandModalIntro')}{' '}
              <code className='text-gray-300'>{selectedNode.node_id}</code>.
            </p>
            <div>
              <label className='block text-xs text-gray-400 mb-1.5'>
                {t('provision.shellCommandLabel')}
              </label>
              <Input
                value={cmdInput}
                onChange={(e) => setCmdInput(e.target.value)}
                placeholder={t('provision.shellCommandPlaceholder')}
                onKeyDown={(e) =>
                  e.key === 'Enter' && cmdInput &&
                  cmdMut.mutate({ id: selectedNode.node_id, cmd: cmdInput })}
                autoFocus
              />
            </div>
            <div className='flex justify-end gap-2'>
              <Button variant='ghost' onClick={() => setSelectedNode(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant='warning'
                loading={cmdMut.isPending}
                disabled={!cmdInput.trim()}
                onClick={() => cmdMut.mutate({ id: selectedNode.node_id, cmd: cmdInput })}
                icon={<Terminal size={13} />}
              >
                {t('provision.sendButton')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function ProvisionPage() {
  const { t } = useTranslation();
  const { data: health, refetch: refetchHealth } = useQuery({
    queryKey: ['provision-health'],
    queryFn: async (): Promise<Health> => {
      // no dedicated mesh.health tool — synthesize from tenants + nodes lists
      const [tenantsR, nodesR] = await Promise.all([
        api.mcp<{ ok: boolean; total?: number; tenants?: unknown[] }>('mesh.tenants', {
          action: 'list',
        }),
        api.mcp<{
          ok: boolean;
          total?: number;
          nodes?: Array<{ status?: string }>;
        }>('mesh.nodes', { action: 'list' }),
      ]);
      const nodes = nodesR.nodes ?? [];
      return {
        tenants: tenantsR.total ?? tenantsR.tenants?.length ?? 0,
        nodes: nodesR.total ?? nodes.length,
        online: nodes.filter((n) => n.status === 'online').length,
      };
    },
    refetchInterval: 30_000,
  });

  return (
    <div className='space-y-6'>
      <PageHeader
        title='MESH Provisioning'
        description={t('provision.pageDescription')}
        icon={Network}
        actions={<RefreshButton onRefresh={refetchHealth} size='sm' />}
      />

      <StatsGrid columns={3}>
        <StatCard
          label={t('provision.statTenants')}
          value={health?.tenants ?? '—'}
          icon={Users}
          variant='info'
        />
        <StatCard
          label={t('provision.statNodesTotal')}
          value={health?.nodes ?? '—'}
          icon={Server}
          variant='default'
        />
        <StatCard
          label={t('provision.statOnline')}
          value={health?.online ?? '—'}
          sub={health
            ? t('provision.percentAvailable', {
              percent: Math.round((health.online / Math.max(health.nodes, 1)) * 100),
            })
            : undefined}
          icon={Wifi}
          variant={health && health.online > 0 ? 'success' : 'default'}
        />
      </StatsGrid>

      <Tabs defaultTab='tenants'>
        <TabList>
          <Tab id='tenants' icon={<Users size={14} />}>{t('provision.tabTenants')}</Tab>
          <Tab id='tokens' icon={<Key size={14} />}>{t('provision.tabTokens')}</Tab>
          <Tab id='nodes' icon={<Server size={14} />}>{t('provision.tabNodes')}</Tab>
        </TabList>

        <div className='mt-4'>
          <TabPanel id='tenants'>
            <TenantsTab />
          </TabPanel>
          <TabPanel id='tokens'>
            <TokensTab />
          </TabPanel>
          <TabPanel id='nodes'>
            <NodesTab />
          </TabPanel>
        </div>
      </Tabs>
    </div>
  );
}
